import React, { useState, ForwardRefRenderFunction, ChangeEvent } from 'react';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { InputProps } from '@mui/material/Input';
import { NumericFormat, PatternFormat } from 'react-number-format';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import OutlinedTextField from './OutlinedTextField';

interface PasswordFieldProps {
  value: string;
  className?: string;
  length?: number;
  inputProps?: Partial<InputProps>;
  numericOnly?: boolean;
  format?: string;
  mask?: string;
  maxLength?: number;
  type?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  id?: string;
  name?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  fullWidth?: boolean;
  autoComplete?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

// eslint-disable-next-line react/function-component-definition
const PasswordField: ForwardRefRenderFunction<HTMLInputElement, PasswordFieldProps> = (
  {
    value,
    className = '',
    length = 100,
    inputProps = {},
    type = 'password',
    numericOnly = false,
    format,
    mask = '_',
    maxLength,
    onChange,
    ...rest
  },
  ref
) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const visibilityAdornment = (
    <InputAdornment position="end" style={{ marginRight: -10 }}>
      <IconButton
        size="large"
        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
        aria-label="toggle password visibility"
      >
        {!isPasswordVisible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  if (numericOnly) {
    return (
      <NumericFormat
        customInput={OutlinedTextField}
        value={value}
        className={className}
        format={format}
        mask={mask}
        allowNegative={false}
        allowLeadingZeros={true}
        isAllowed={(values) => {
          if (!maxLength) return true;
          return values.value.length <= maxLength;
        }}
        slotProps={{
          htmlInput: {
            maxLength: length,
          },
          input: {
            type: isPasswordVisible ? 'text' : 'password',
            ...inputProps,
            endAdornment: visibilityAdornment,
          },
        }}
        inputRef={ref}
        onChange={onChange as any}
        {...rest}
      />
    );
  }

  if (format) {
    return (
      <PatternFormat
        customInput={OutlinedTextField}
        value={value}
        className={className}
        format={format}
        mask={mask}
        slotProps={{
          htmlInput: {
            maxLength: length,
          },
          input: {
            type: isPasswordVisible ? 'text' : 'password',
            ...inputProps,
            endAdornment: visibilityAdornment,
          },
        }}
        inputRef={ref}
        onChange={onChange as any}
        {...rest}
      />
    );
  }

  return (
    <OutlinedTextField
      value={value}
      slotProps={{
        htmlInput: {
          maxLength: length,
        },
        input: {
          type: isPasswordVisible ? 'text' : 'password',
          ...inputProps,
          endAdornment: visibilityAdornment,
        },
      }}
      className={className}
      inputRef={ref}
      onChange={onChange}
      {...rest}
    />
  );
};

export default React.forwardRef(PasswordField);
